import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, switchMap } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { Camera as NativeCamera, CameraResultType } from '@capacitor/camera';
import { GpsV2Service, UserService, Utils } from '@app/services';
import { UserRegisterIneRes } from '@interfaces/app';
import { environment } from 'src/environments/environment';
import { HEADER_DONT_SAVE, HEADER_DONT_SAVE_REQ } from '@app/constants';
import { UserTokens } from '@app/interfaces';


@Injectable({ providedIn: 'root' })
export class UserOnboardingService {

   constructor(
      private readonly http: HttpClient,
      private readonly userServ: UserService,
      private readonly gpsServ: GpsV2Service,
      private readonly utilServ: Utils,
   ) { }

   getUserInfo() {
      return this.http.get<UserRegisterIneRes>(`${environment.apiUrlBase}/user/edit/info`, { headers: HEADER_DONT_SAVE_REQ });
   }

   /** `Paso 1`: Subir documentos de INE y extraer datos OCR */
   uploadIne(inefront: File | Blob, ineback: File | Blob) {
      const data = new FormData();

      data.set('f', inefront);
      data.set('b', ineback);

      return this.http.post<UserRegisterIneRes>(`${environment.apiUrlBase}/user/edit/ine`, data, HEADER_DONT_SAVE);
   }

   /** Actualizar CURP y RFC */
   updateIds(curp: string, rfc?: string) {
      return this.http.put<UserRegisterIneRes>(`${environment.apiUrlBase}/user/edit/id`, { curp, rfc }, HEADER_DONT_SAVE);
   }

   /** Validar CURP y obtener información del usuario */
   validateInfoByCurp(curp: string, paramsToSend: any) {
      return this.http.put<any>(`${environment.apiUrlBase}/user/edit/validate-info-curp`, { curp, paramsToSend }, HEADER_DONT_SAVE);
   }

   /** Actualizar país de nacimiento */
   updateInfoBirthCountry(birthCountry: string) {
      return this.http.put<any>(`${environment.apiUrlBase}/user/edit/up-info-birth-country`, birthCountry, HEADER_DONT_SAVE);
   }

   /** Actualizar UserInfo */
   updateUserInfo(body: any) {
      return this.http.put<UserRegisterIneRes>(`${environment.apiUrlBase}/user/edit/up-info`, body, HEADER_DONT_SAVE);
   }

   /** Actualizar Dirección */
   updateUserAddress(address: any, job: any) {
      return this.http.put<UserRegisterIneRes>(`${environment.apiUrlBase}/user/edit/up-address`, { address, job}, HEADER_DONT_SAVE);
   }

   getSuburbsByZipCode(zipCode: string) {
      return this.http.get<{ cp: "05100", asentamiento: string[], tipo_asentamiento: string, municipio: string, estado: string, ciudad: string, pais: string }>(
         `${environment.apiUrlBase}/user/edit/zc/${zipCode}`);
   }

   openMobileCamera() {
      return new Observable<string>(res => {
         // NativeCamera.requestPermissions().then(ok => {
         //    console.log(ok);

            NativeCamera.getPhoto({ quality: 100, allowEditing: false, resultType: CameraResultType.DataUrl }).then(image => {
               // image.webPath will contain a path that can be set as an image src.
               // You can access the original file using image.path, which can be
               // passed to the Filesystem API to read the raw data of the image,
               // if desired (or pass resultType: CameraResultType.Base64 to getPhoto)
               res.next(image.dataUrl);
               // Can be set to the src of an image now
               // imageElement.src = imageUrl;
            }).finally(() => res.complete());
         // }).catch(err => {
         //    console.log(err);
         //    res.error();
         //    res.complete();
         // })
      });
   }

   /** Firmar contrato de forma digital */
   signatureContract() {
      return this.gpsServ.getOrRequestCoordinates().pipe(
         map(({ latitude, longitude, accuracy}) => {
            const params = this.utilServ.utf8ToBase64(`${longitude}|${latitude}|${accuracy}`);
            const headers = HEADER_DONT_SAVE_REQ.set('tk', params);

            return {
               url: `${environment.apiUrlBase}/user/edit/sign-contract`,
               headers
            }
         }),
         switchMap(({ url, headers}) => 
            this.http.post<{ url: string }>(url, '', { headers })
         ),
         catchError((err) => {
            console.error('Error al obtener las coordenadas GPS:', err);
            return throwError(() => new Error('Error al obtener las coordenadas GPS'));
         })
      )
   }

   /** Subir archivo de firma */
   uploadSignature(dataURL: string): Observable<{ url: string }> {
     const blobBin = this.utilServ.base64ToUtf8(dataURL.split(',')[1]);
     const array = [];

     for (let i = 0; i < blobBin.length; i++) {
       array.push(blobBin.charCodeAt(i));
     }

     const blobSignature: any = new Blob([new Uint8Array(array)], { type: 'image/png' });
     blobSignature.lastModifiedDate = new Date();
     blobSignature.name = 'signature';

     const signatureForm = new FormData();
     signatureForm.set('signature', blobSignature);

     return this.gpsServ.getOrRequestCoordinates().pipe(
       switchMap(coords => {
         if (!coords) {
           console.error('Coordenadas GPS no disponibles.');
           return throwError(() => new Error('Ubicación no disponible'));
         }

         const { latitude, longitude, accuracy } = coords;
         const params = this.utilServ.utf8ToBase64(`${longitude}|${latitude}|${accuracy}`);
         const headers = HEADER_DONT_SAVE_REQ.set('tk', params);

         return this.http.post<{ url: string }>(
           `${environment.apiUrlBase}/user/edit/sign`,
           signatureForm,
           { headers }
         );
       }),
       catchError(err => {
         console.error('Error al obtener ubicación para firma:', err);
         return throwError(() => err);
       })
     );
   }

   /** Crear Wallet de usuario en proceso de registro */
   createSTPWallet() {
      return this.http.post<{ balance: 0; clabe: string; stateSTP: string; status: string; }>(
         `${environment.apiUrlBase}/wallet/create`, {}, HEADER_DONT_SAVE).pipe(map((stp) => {
            this.userServ.cleanUserCache();
            return stp;
         }));
   }

   /** Función para validar la contraseña */
   otpPassword(email: string, pw: string) {

      const mail = email.trim().toLowerCase();
      const sd = `${mail}|${pw}`;

      const params = { ctd: this.utilServ.utf8ToBase64(sd) };

      return this.http.post<UserTokens>(`${environment.apiUrlBase}/auth/verify-pass`, params, HEADER_DONT_SAVE)
   }

   /** Función para subir archivos del registro en el S3 */
   uploadDocument(file: File, type: 'idCardFront' | 'idCardBack' | 'otherDocument') {
      const data = new FormData();
      const headers = HEADER_DONT_SAVE_REQ;

      data.set(type, file);

      return this.http.post<{ url: string }>(`${environment.apiUrlBase}/user/up-docs-register`, data, { headers });
   }

   /** Actualizar tipo de documento a subir */
   typeDocument(typeDocument: string) {
      return this.http.put(`${environment.apiUrlBase}/user/set-type-document`, typeDocument , HEADER_DONT_SAVE);
   }

   saveListsUser(fullName: string) {
      return this.http.post(`${environment.apiUrlBase}/user/save-lists-user`, { fullName }, { headers: HEADER_DONT_SAVE_REQ });
   }

   setMaxOperationPerMonth(objectToSend: Object) {
      return this.http.post(`${environment.apiUrlBase}/user/set-max-operation`, { objectToSend }, { headers: HEADER_DONT_SAVE_REQ });
   }

}
